import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { getTimeMultiplier } from '../lib/scores'

function getLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function Confetti({ active }) {
  const canvasRef = useRef(null)
  const animRef = useRef(null)

  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const pieces = Array.from({ length: 150 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 100,
      r: Math.random() * 7 + 3,
      color: ['#f0cc7a', '#c9a84c', '#4caf7d', '#9b5ce0', '#e05c5c', '#5c8fe0', '#fff'][Math.floor(Math.random() * 7)],
      tilt: Math.random() * 10 - 5,
      tiltSpeed: Math.random() * 0.15 + 0.05,
      speed: Math.random() * 4 + 2,
      opacity: 1,
      wobble: Math.random() * Math.PI * 2,
    }))

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      pieces.forEach(p => {
        p.y += p.speed
        p.wobble += 0.05
        p.tilt += p.tiltSpeed
        p.opacity -= 0.003
        if (p.y > canvas.height || p.opacity <= 0) {
          p.y = -20; p.x = Math.random() * canvas.width; p.opacity = 1
        }
        ctx.save()
        ctx.globalAlpha = Math.max(0, p.opacity)
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.ellipse(p.x + Math.sin(p.wobble) * 4, p.y, p.r, p.r * 0.45, p.tilt, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      })
      animRef.current = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(animRef.current)
  }, [active])

  if (!active) return null
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 1000, pointerEvents: 'none', width: '100vw', height: '100vh' }} />
}

export default function OnboardingTutorial({ userId, onComplete }) {
  const [step, setStep] = useState(0)
  const [done, setDone] = useState(false)
  const [description, setDescription] = useState('Set up my LifeScore account')
  const [evaluating, setEvaluating] = useState(false)
  const [preview, setPreview] = useState(null)
  const [saving, setSaving] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [evalError, setEvalError] = useState('')

  useEffect(() => {
    if (userId) {
      const seen = localStorage.getItem(`lifescore_tutorial_${userId}`)
      if (seen) { setDone(true); onComplete?.() }
    }
  }, [userId])

  async function handleAnalyze() {
    if (!description.trim()) return
    setEvaluating(true)
    setEvalError('')
    try {
      const res = await fetch('/api/evaluate-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: description.trim(), timeMinutes: 30, userId })
      })
      const data = await res.json()
      if (data.error) { setEvalError(data.error); setEvaluating(false); return }
      const timeMult = getTimeMultiplier(30)
      const finalPoints = data.flagged ? 0 : Math.round(data.base_points * timeMult)
      setPreview({ ...data, finalPoints })
      setStep(3)
    } catch (e) { setEvalError('Something went wrong. Try again.') }
    setEvaluating(false)
  }

  async function handleSave() {
    if (!preview) return
    setSaving(true)
    await supabase.from('manual_tasks').insert({
      user_id: userId,
      date: getLocalDate(),
      description: description.trim(),
      category: preview.category,
      area: preview.area,
      base_points: preview.base_points,
      time_minutes: 30,
      final_points: preview.finalPoints,
      flagged: preview.flagged,
      flag_reason: preview.flag_reason || '',
    })
    setSaving(false)
    setStep(4)
    setShowConfetti(true)
    setTimeout(() => setShowConfetti(false), 5000)
  }

  function finish() {
    localStorage.setItem(`lifescore_tutorial_${userId}`, 'true')
    setDone(true)
    setTimeout(() => onComplete?.(), 300)
  }

  if (done) return null

  return (
    <>
      <style>{`
        @keyframes pulse { 0%,100% { box-shadow: 0 0 20px rgba(201,168,76,0.5); } 50% { box-shadow: 0 0 50px rgba(201,168,76,1); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bounce { from { transform:translateY(0); } to { transform:translateY(8px); } }
      `}</style>

      <Confetti active={showConfetti} />

      <div style={s.screen}>

        {/* STEP 0 — WELCOME */}
        {step === 0 && (
          <div style={s.center}>
            <div style={s.bigTitle}>WELCOME TO{'\n'}LIFESCORE</div>
            <div style={s.sub}>Let's earn your first points.{'\n'}Takes less than a minute.</div>
            <div style={{ ...s.goldBtn, animation: 'pulse 2s infinite' }} onClick={() => setStep(1)}>
              GET STARTED →
            </div>
          </div>
        )}

        {/* STEP 1 — DESCRIBE */}
        {step === 1 && (
          <div style={s.center}>
            <div style={s.stepLabel}>STEP 1 OF 3</div>
            <div style={s.title}>Describe what you did today</div>
            <div style={s.hint}>We've pre-filled one for you — feel free to edit it or leave it as is</div>
            <textarea
              style={s.textarea}
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              autoFocus
            />
            <div style={{ ...s.goldBtn, opacity: !description.trim() ? 0.5 : 1, animation: description.trim() ? 'pulse 2s infinite' : 'none' }}
              onClick={() => description.trim() && setStep(2)}>
              Looks good →
            </div>
          </div>
        )}

        {/* STEP 2 — ANALYZE */}
        {step === 2 && (
          <div style={s.center}>
            <div style={s.stepLabel}>STEP 2 OF 3</div>
            <div style={s.title}>Now let's score your task</div>
            <div style={s.hint}>Our AI will analyze what you did and assign points to your life areas</div>
            <div style={s.taskPreview}>"{description}"</div>
            {evalError ? <div style={s.error}>{evalError}</div> : null}
            <div style={{ ...s.goldBtn, opacity: evaluating ? 0.6 : 1, animation: evaluating ? 'none' : 'pulse 2s infinite' }}
              onClick={handleAnalyze}>
              {evaluating ? 'Analyzing...' : 'Analyze Task →'}
            </div>
          </div>
        )}

        {/* STEP 3 — SAVE */}
        {step === 3 && preview && (
          <div style={s.center}>
            <div style={s.stepLabel}>STEP 3 OF 3</div>
            <div style={s.title}>Your task has been scored!</div>
            <div style={s.previewCard}>
              <div style={s.previewArea}>{preview.category} · {preview.area}</div>
              <div style={s.previewPoints}>+{preview.finalPoints} pts</div>
              <div style={s.previewReason}>{preview.reasoning}</div>
            </div>
            <div style={{ ...s.goldBtn, opacity: saving ? 0.6 : 1, animation: saving ? 'none' : 'pulse 2s infinite' }}
              onClick={handleSave}>
              {saving ? 'Saving...' : 'Save & Earn Points →'}
            </div>
          </div>
        )}

        {/* STEP 4 — CELEBRATE */}
        {step === 4 && (
          <div style={s.center}>
            <div style={s.bigTitle}>FIRST POINTS{'\n'}EARNED!</div>
            <div style={s.sub}>You're on the board.{'\n'}Keep logging every day to level up!</div>
            <div style={{ ...s.goldBtn, animation: 'pulse 2s infinite' }} onClick={finish}>
              LET'S GO →
            </div>
          </div>
        )}

      </div>
    </>
  )
}

const s = {
  screen: { position: 'fixed', inset: 0, zIndex: 600, background: '#0d0d14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', padding: '0 32px', maxWidth: '420px', width: '100%', animation: 'fadeIn 0.4s ease' },
  bigTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', letterSpacing: '4px', color: '#f0cc7a', lineHeight: 1.1, textAlign: 'center', whiteSpace: 'pre-line' },
  stepLabel: { fontSize: '11px', letterSpacing: '3px', color: '#6b6860', textTransform: 'uppercase' },
  title: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px', color: '#e8e6df', textAlign: 'center', lineHeight: 1.2 },
  hint: { fontSize: '14px', color: '#6b6860', textAlign: 'center', lineHeight: '1.6', whiteSpace: 'pre-line' },
  sub: { fontSize: '15px', color: '#a09e97', textAlign: 'center', lineHeight: '1.7', whiteSpace: 'pre-line' },
  textarea: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '14px', padding: '16px', color: '#e8e6df', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'none', lineHeight: '1.5', boxSizing: 'border-box' },
  taskPreview: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '16px 20px', color: '#e8e6df', fontSize: '15px', fontStyle: 'italic', textAlign: 'center', width: '100%', boxSizing: 'border-box' },
  previewCard: { background: 'rgba(76,175,125,0.08)', border: '1px solid rgba(76,175,125,0.3)', borderRadius: '14px', padding: '20px', width: '100%', boxSizing: 'border-box' },
  previewArea: { fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4caf7d', marginBottom: '6px', fontWeight: '600' },
  previewPoints: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', color: '#4caf7d', lineHeight: 1, marginBottom: '8px' },
  previewReason: { fontSize: '13px', color: '#6b6860', lineHeight: '1.5' },
  goldBtn: { width: '100%', padding: '18px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', borderRadius: '14px', textAlign: 'center', cursor: 'pointer' },
  error: { fontSize: '13px', color: '#e05c5c', background: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '10px', padding: '12px 16px', width: '100%', boxSizing: 'border-box', textAlign: 'center' },
}
